import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { SplineHero } from "@/components/SplineHero";
import { MentorCard } from "@/components/MentorCard";
import { BookingModal } from "@/components/BookingModal";
import { Toaster } from "sonner";
import { CalendarPlus, ArrowRight, TrendingUp, Users, Target, MessageCircle, Instagram, Play } from "lucide-react";
import { VideoModal, hasVideo } from "@/components/VideoModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

const RESULTS = [
    { mentor: "Ángelo Vargas", value: "$140K+", label: "Mejor mes de ventas", channel: "Director" },
    { mentor: "Cristian Cruz", value: "$60K+", label: "Mejor mes de ventas", channel: "Escalamiento" },
    { mentor: "Yonathan Abac", value: "$50K+", label: "Mejor mes de ventas", channel: "Contenido" },
    { mentor: "Leo Gaba", value: "$48K+", label: "Mejor mes de ventas", channel: "Comunicación" },
    { mentor: "Ana Milagros", value: "$35K+", label: "Mejor mes de ventas", channel: "Comunidad" },
    { mentor: "Lester Quino", value: "$33K+", label: "Mejor mes de ventas", channel: "Logística" },
];

const STEPS = [
    { icon: Users, title: "Elige tu mentor", text: "Selecciona al especialista según el punto exacto donde estás detenido: contenido, conversación o cierre." },
    { icon: Target, title: "Agenda tu llamada", text: "Reserva un horario disponible en el calendario del mentor. Todo queda sincronizado en tiempo real." },
    { icon: TrendingUp, title: "Aplica y vende", text: "Recibe la estrategia, ejecuta y escala tus ventas con acompañamiento real del Texas Team." },
];

export default function Landing() {
    const [mentors, setMentors] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [preselect, setPreselect] = useState(null);
    const [dirVideo, setDirVideo] = useState(false);

    useEffect(() => {
        api.get("/mentors").then(({ data }) => setMentors(data)).catch(() => {});
    }, []);

    const openBooking = (mentor = null) => {
        setPreselect(mentor);
        setModalOpen(true);
    };

    return (
        <div className="min-h-screen bg-hero-bg">
            <Toaster theme="dark" position="top-center" richColors />
            <Navbar onBook={() => openBooking(null)} />

            {/* HERO */}
            <section className="relative flex min-h-screen items-end overflow-hidden bg-hero-bg">
                <SplineHero />
                <div className="absolute inset-0 z-[1] bg-gradient-to-t from-hero-bg via-hero-bg/40 to-transparent" />
                <div className="pointer-events-none relative z-10 w-full max-w-[92%] px-6 pb-16 pt-32 sm:max-w-2xl md:px-12 lg:max-w-4xl">
                    <p className="mb-4 animate-fade-up text-xs font-semibold uppercase tracking-[0.28em] text-primary opacity-0" style={{ animationDelay: "0.1s" }}>
                        Texas Team · Director Ángelo Vargas
                    </p>
                    <h1
                        className="mb-3 animate-fade-up text-[clamp(1.5rem,4vw,3rem)] font-bold uppercase leading-[1.08] tracking-[-0.03em] text-foreground opacity-0 md:mb-5"
                        style={{ animationDelay: "0.2s" }}
                    >
                        Domina el arte<br />de la <span className="text-primary">reventa<br />online</span>
                    </h1>
                    <p className="mb-3 animate-fade-up text-[clamp(1.05rem,2.5vw,1.7rem)] font-light text-foreground/80 opacity-0 md:mb-6" style={{ animationDelay: "0.4s" }}>
                        Mentoría privada con quienes ya lo lograron.
                    </p>
                    <p className="mb-6 animate-fade-up text-[clamp(0.9rem,1.5vw,1.15rem)] font-light text-muted-foreground opacity-0 md:mb-8" style={{ animationDelay: "0.55s" }}>
                        Estrategia, proveedores, ventas y contenido. Elige al mentor que entiende el punto exacto donde estás detenido y agenda tu llamada hoy.
                    </p>
                    <div className="flex animate-fade-up flex-wrap gap-3 opacity-0" style={{ animationDelay: "0.7s" }}>
                        <button
                            onClick={() => openBooking(null)}
                            data-testid="hero-book-btn"
                            className="pointer-events-auto cursor-pointer rounded-full bg-primary px-7 py-3.5 text-sm font-bold text-primary-foreground shadow-[0_0_22px_hsl(119_99%_46%/0.4)] transition-all hover:brightness-110 active:scale-95 md:px-9 md:py-4"
                        >
                            Agendar llamada
                        </button>
                        <a
                            href="#equipo"
                            className="pointer-events-auto flex cursor-pointer items-center gap-2 rounded-full border border-white/20 bg-transparent px-7 py-3.5 text-sm font-semibold text-white transition-all hover:border-white hover:bg-white/5 active:scale-95 md:px-9 md:py-4"
                        >
                            Ver el equipo <ArrowRight size={16} />
                        </a>
                    </div>
                    <p className="mt-6 animate-fade-up text-xs font-light text-muted-foreground/60 opacity-0" style={{ animationDelay: "0.85s" }}>
                        Coaching privado dirigido por Ángelo Vargas (@avendedortx) · 5 mentores reales
                    </p>
                </div>
            </section>

            {/* RESULTS */}
            <section id="resultados" className="relative border-y border-border bg-background py-20">
                <div className="mx-auto max-w-6xl px-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">La experiencia, en cifras</p>
                        <h2 className="mt-2 text-2xl font-bold uppercase tracking-tight text-foreground sm:text-4xl">Resultados reales por mentor</h2>
                    </motion.div>
                    <div className="mt-10 grid gap-4 sm:grid-cols-3">
                        {RESULTS.map((r, i) => (
                            <motion.div
                                key={r.mentor}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="rounded-2xl border border-border bg-card p-6 transition-all hover:border-primary/40"
                            >
                                <p className="font-mono text-4xl font-bold text-primary">{r.value}</p>
                                <p className="mt-2 text-sm text-foreground">{r.label}</p>
                                <div className="mt-4 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
                                    <span>{r.mentor}</span>
                                    <span className="rounded-full bg-secondary px-2 py-0.5">{r.channel}</span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TEAM */}
            <section id="equipo" className="relative bg-hero-bg py-20">
                <div className="mx-auto max-w-6xl px-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">Quién te acompaña</p>
                        <h2 className="mt-2 max-w-2xl text-2xl font-bold uppercase tracking-tight text-foreground sm:text-4xl">
                            Elige a quien entiende dónde estás detenido
                        </h2>
                    </motion.div>
                    {(() => {
                        const director = mentors.find((m) => m.is_director);
                        const rest = mentors.filter((m) => !m.is_director);
                        return (
                            <div className="mt-10 space-y-6">
                                {director && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                                        data-testid={`mentor-card-${director.username}`}
                                        className="relative grid overflow-hidden rounded-3xl border border-primary/40 bg-card shadow-[0_0_40px_hsl(119_99%_46%/0.12)] md:grid-cols-[300px_1fr]"
                                    >
                                        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
                                        <div className="relative aspect-[4/5] overflow-hidden md:aspect-auto">
                                            <img src={resolveUrl(director.photo_url) || "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=940"} alt={director.display_name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent md:bg-gradient-to-r" />
                                        </div>
                                        <div className="flex flex-col justify-center p-6 md:p-10">
                                            <span className="w-fit rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary-foreground">Director del Texas Team</span>
                                            <h3 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{director.display_name}</h3>
                                            <p className="mt-1 text-sm font-semibold uppercase tracking-[0.16em] text-primary">{director.specialty}</p>
                                            <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">{director.bio}</p>
                                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                                <button onClick={() => openBooking(director)} data-testid={`book-mentor-${director.username}`} className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold uppercase tracking-widest text-primary-foreground shadow-[0_0_20px_hsl(119_99%_46%/0.4)] transition-all hover:brightness-110 active:scale-95">
                                                    <CalendarPlus size={16} /> Agendar con el Director
                                                </button>
                                                {hasVideo(director) && (
                                                    <button onClick={() => setDirVideo(true)} data-testid={`play-video-${director.username}`} className="flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm font-semibold text-foreground transition-all hover:border-primary hover:text-primary">
                                                        <Play size={15} fill="currentColor" /> Ver testimonio
                                                    </button>
                                                )}
                                                {director.instagram && (
                                                    <a href={`https://instagram.com/${director.instagram}`} target="_blank" rel="noreferrer" className="rounded-full border border-border p-3 text-muted-foreground transition-all hover:border-primary hover:text-primary"><Instagram size={16} /></a>
                                                )}
                                                {director.whatsapp && (
                                                    <a href={`https://wa.me/${director.whatsapp}`} target="_blank" rel="noreferrer" className="rounded-full border border-border p-3 text-muted-foreground transition-all hover:border-primary hover:text-primary"><MessageCircle size={16} /></a>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                                <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                    {rest.map((m, i) => (
                                        <MentorCard key={m.id} mentor={m} index={i} onBook={openBooking} />
                                    ))}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section id="como-funciona" className="relative border-y border-border bg-background py-20">
                <div className="mx-auto max-w-6xl px-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary">Cómo funciona</p>
                        <h2 className="mt-2 text-2xl font-bold uppercase tracking-tight text-foreground sm:text-4xl">Del producto a la venta, en 3 pasos</h2>
                    </motion.div>
                    <div className="mt-10 grid gap-6 md:grid-cols-3">
                        {STEPS.map((s, i) => (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.6, delay: i * 0.1 }}
                                className="rounded-2xl border border-border bg-card p-6"
                            >
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                                    <s.icon size={22} />
                                </div>
                                <span className="mt-4 block font-mono text-xs text-muted-foreground">0{i + 1}</span>
                                <h3 className="mt-1 text-lg font-bold text-foreground">{s.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CONTACT / CTA */}
            <section id="contacto" className="relative overflow-hidden bg-hero-bg py-24">
                <div className="mx-auto max-w-4xl px-6 text-center">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-3xl font-bold uppercase leading-tight tracking-tight text-foreground sm:text-5xl"
                    >
                        ¿Listo para tu <span className="text-primary">siguiente venta</span>?
                    </motion.h2>
                    <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
                        Cuéntanos qué estás intentando vender. Te conectamos con el mentor indicado según tu producto, canal y etapa actual.
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <button
                            onClick={() => openBooking(null)}
                            data-testid="cta-book-btn"
                            className="rounded-full bg-primary px-8 py-4 text-sm font-bold uppercase tracking-widest text-primary-foreground shadow-[0_0_22px_hsl(119_99%_46%/0.4)] transition-all hover:brightness-110 active:scale-95"
                        >
                            Agendar con un mentor
                        </button>
                        <a
                            href="https://wa.me/13468897560?text=Hola%20Ángelo,%20quiero%20información%20sobre%20AVENDOR%20Texas%20Team."
                            target="_blank"
                            rel="noreferrer"
                            data-testid="cta-whatsapp"
                            className="flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-all hover:border-white hover:bg-white/5"
                        >
                            <MessageCircle size={16} /> Escribir por WhatsApp
                        </a>
                    </div>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="border-t border-border bg-background py-10">
                <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 sm:flex-row">
                    <div className="flex items-center gap-3">
                        <img src="/av-logo.png" alt="AV" className="h-8 w-auto select-none" />
                        <div>
                            <p className="text-lg font-bold text-foreground">AVENDOR <span className="text-primary">Texas Team</span></p>
                            <p className="text-xs text-muted-foreground">Director · Ángelo Vargas</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <a href="https://instagram.com/avendedortx" target="_blank" rel="noreferrer" className="rounded-full border border-border p-2.5 text-muted-foreground transition-all hover:border-primary hover:text-primary">
                            <Instagram size={16} />
                        </a>
                        <a href="https://wa.me/13468897560" target="_blank" rel="noreferrer" className="rounded-full border border-border p-2.5 text-muted-foreground transition-all hover:border-primary hover:text-primary">
                            <MessageCircle size={16} />
                        </a>
                    </div>
                </div>
            </footer>

            <BookingModal open={modalOpen} onClose={() => setModalOpen(false)} mentors={mentors} preselect={preselect} />
            <VideoModal open={dirVideo} onClose={() => setDirVideo(false)} mentor={mentors.find((m) => m.is_director)} />
        </div>
    );
}
